import type { HomeProblem } from "./problems-data";
import { PROBLEMS_DATA } from "./problems-data";
import { PRICING_DATA } from "./pricing-data";

export interface Room {
  id: string;
  label: string;
  icon: string;
  symptoms: Symptom[];
}

export interface Symptom {
  id: string;
  label: string;
  problemSlugs: string[];
}

export interface DiagnoseResult {
  problem: HomeProblem;
  costLow: number | null;
  costHigh: number | null;
  tradeName: string;
}

export const ROOMS: Room[] = [
  {
    id: "kitchen",
    label: "Kitchen",
    icon: "🍳",
    symptoms: [
      {
        id: "water-plumbing",
        label: "Water or plumbing issue",
        problemSlugs: [
          "dripping-tap", "blocked-drain", "leaking-pipe-under-sink",
          "low-water-pressure", "sink-waste-slow-to-drain",
        ],
      },
      {
        id: "appliance",
        label: "Appliance not working",
        problemSlugs: [
          "washing-machine-not-spinning", "dishwasher-not-draining",
          "oven-not-heating-up", "fridge-not-cooling",
          "tumble-dryer-not-drying", "freezer-icing-up",
          "washing-machine-leaking",
        ],
      },
      {
        id: "electrical",
        label: "Electrical problem",
        problemSlugs: [
          "lights-flickering", "plug-socket-not-working",
          "burning-smell-from-socket", "tripping-circuit-breaker",
          "power-outage-in-one-room",
        ],
      },
      {
        id: "cabinets-worktops",
        label: "Cabinets or worktops",
        problemSlugs: [
          "kitchen-cupboard-door-hanging-off", "worktop-swelling-near-sink",
          "kitchen-drawer-stuck", "kickboard-coming-loose",
          "kitchen-unit-falling-off-wall", "soft-close-hinges-broken",
        ],
      },
      {
        id: "tiles-flooring",
        label: "Tiles or flooring",
        problemSlugs: [
          "loose-floor-tile", "tiles-lifting-from-floor",
          "floor-tiles-cracking", "grout-turning-black",
          "laminate-flooring-lifting", "vinyl-flooring-bubbling",
        ],
      },
      {
        id: "damp-decoration",
        label: "Damp, mould or decoration",
        problemSlugs: [
          "peeling-paint-on-walls", "damp-stain-on-wall",
          "cracked-plaster-on-wall", "blown-plaster",
          "wallpaper-peeling-off", "grease-build-up-in-kitchen",
        ],
      },
      {
        id: "pests",
        label: "Pests",
        problemSlugs: ["ants-in-kitchen", "mice-in-the-house"],
      },
    ],
  },
  {
    id: "bathroom",
    label: "Bathroom",
    icon: "🚿",
    symptoms: [
      {
        id: "water-leak",
        label: "Water leak",
        problemSlugs: [
          "dripping-tap", "leaking-pipe-under-sink", "shower-tray-leaking",
          "shower-screen-leaking", "bath-panel-coming-loose",
          "low-water-pressure",
        ],
      },
      {
        id: "mould-damp",
        label: "Mould or damp",
        problemSlugs: [
          "mould-on-bathroom-ceiling", "black-mould-in-bathroom",
          "damp-patch-on-ceiling", "damp-stain-on-wall",
          "condensation-between-double-glazing",
        ],
      },
      {
        id: "drainage",
        label: "Drainage issue",
        problemSlugs: [
          "blocked-drain", "slow-draining-bath",
          "sink-waste-slow-to-drain", "gurgling-drains",
        ],
      },
      {
        id: "tiles",
        label: "Cracked or loose tiles",
        problemSlugs: [
          "cracked-bathroom-tile", "grout-turning-black",
          "shower-tiles-coming-away", "water-seeping-behind-tiles",
        ],
      },
      {
        id: "toilet",
        label: "Toilet problem",
        problemSlugs: ["toilet-keeps-running", "toilet-wobbling"],
      },
      {
        id: "heating-hot-water",
        label: "Heating or hot water",
        problemSlugs: [
          "no-hot-water", "electric-shower-not-heating",
          "radiator-not-heating-up",
        ],
      },
      {
        id: "sealant-extractor",
        label: "Sealant or ventilation",
        problemSlugs: [
          "cracked-bathroom-sealant", "bathroom-extractor-fan-not-working",
          "stubborn-limescale-in-bathroom",
        ],
      },
    ],
  },
  {
    id: "bedroom",
    label: "Bedroom",
    icon: "🛏️",
    symptoms: [
      {
        id: "damp-decoration",
        label: "Damp, mould or decoration",
        problemSlugs: [
          "peeling-paint-on-walls", "cracked-plaster-on-wall",
          "wallpaper-peeling-off", "damp-stain-on-wall",
          "blown-plaster", "damp-patch-through-plaster",
          "bubbling-plaster",
        ],
      },
      {
        id: "window",
        label: "Window problem",
        problemSlugs: [
          "draughty-windows", "condensation-between-double-glazing",
          "window-not-closing-properly", "draught-coming-through-window",
          "window-handle-broken", "cracked-window-pane",
          "window-seal-perished",
        ],
      },
      {
        id: "door",
        label: "Door problem",
        problemSlugs: [
          "sticking-door", "warped-door-frame",
          "door-handle-loose", "squeaky-door-hinge",
        ],
      },
      {
        id: "flooring",
        label: "Flooring issue",
        problemSlugs: [
          "creaking-floorboard", "laminate-flooring-lifting",
          "carpet-coming-loose-at-edges", "wooden-floor-scratches",
          "creaky-wooden-floor", "carpet-stains-not-coming-out",
        ],
      },
      {
        id: "heating",
        label: "Heating problem",
        problemSlugs: [
          "radiator-not-heating-up", "radiators-cold-at-bottom",
          "house-cold-despite-heating", "draughty-rooms-upstairs",
        ],
      },
      {
        id: "electrical",
        label: "Electrical issue",
        problemSlugs: [
          "lights-flickering", "plug-socket-not-working",
          "power-outage-in-one-room",
        ],
      },
      {
        id: "pests",
        label: "Pests",
        problemSlugs: [
          "bed-bugs", "moth-infestation", "mice-in-the-house",
        ],
      },
    ],
  },
  {
    id: "living-room",
    label: "Living Room",
    icon: "🛋️",
    symptoms: [
      {
        id: "walls-decoration",
        label: "Walls or decoration",
        problemSlugs: [
          "peeling-paint-on-walls", "cracked-ceiling-paint",
          "wallpaper-peeling-off", "damp-stain-on-wall",
          "yellowing-paint-on-ceiling", "cracked-plaster-on-wall",
          "blown-plaster", "bubbling-plaster",
        ],
      },
      {
        id: "flooring",
        label: "Flooring issue",
        problemSlugs: [
          "creaking-floorboard", "laminate-flooring-lifting",
          "carpet-coming-loose-at-edges", "vinyl-flooring-bubbling",
          "wooden-floor-scratches", "creaky-wooden-floor",
          "loose-floor-tile", "carpet-stains-not-coming-out",
        ],
      },
      {
        id: "heating",
        label: "Heating problem",
        problemSlugs: [
          "radiator-not-heating-up", "radiators-cold-at-bottom",
          "noisy-central-heating", "thermostat-not-working",
          "central-heating-slow-to-warm-up", "house-cold-despite-heating",
        ],
      },
      {
        id: "electrical",
        label: "Electrical issue",
        problemSlugs: [
          "lights-flickering", "plug-socket-not-working",
          "burning-smell-from-socket", "light-switch-buzzing",
          "power-outage-in-one-room", "tripping-circuit-breaker",
        ],
      },
      {
        id: "fireplace-chimney",
        label: "Fireplace or chimney",
        problemSlugs: [
          "smoke-coming-back-into-room", "chimney-not-drawing-properly",
          "soot-falling-into-fireplace", "strong-smell-from-chimney",
        ],
      },
      {
        id: "doors-windows",
        label: "Doors or windows",
        problemSlugs: [
          "sticking-door", "draughty-windows",
          "draught-coming-through-window", "condensation-between-double-glazing",
          "window-not-closing-properly",
        ],
      },
      {
        id: "fixtures",
        label: "Fixtures or fittings",
        problemSlugs: [
          "shelf-falling-off-wall", "curtain-rail-pulling-away",
          "door-handle-loose", "squeaky-door-hinge",
          "damaged-skirting-board",
        ],
      },
      {
        id: "tv-aerial",
        label: "TV or aerial",
        problemSlugs: [
          "tv-signal-breaking-up", "satellite-dish-no-signal",
          "no-signal-on-some-channels", "tv-picture-pixelating",
        ],
      },
    ],
  },
  {
    id: "exterior",
    label: "Exterior",
    icon: "🏠",
    symptoms: [
      {
        id: "cracks-structural",
        label: "Cracks or structural damage",
        problemSlugs: [
          "crack-in-exterior-wall", "bowing-wall", "subsidence-signs",
          "crumbling-mortar", "crumbling-brickwork", "cracked-bricks-in-wall",
          "mortar-crumbling-between-bricks", "brick-wall-leaning",
          "spalling-brickwork", "foundation-cracks",
        ],
      },
      {
        id: "damp-render",
        label: "Damp or render",
        problemSlugs: [
          "damp-penetrating-through-wall", "damp-on-brick-wall",
          "render-cracking-on-wall", "render-falling-off-wall",
          "damp-through-render", "render-turning-green",
          "pebbledash-falling-off", "render-bulging",
        ],
      },
      {
        id: "drainage",
        label: "Drainage problem",
        problemSlugs: [
          "blocked-outside-drain", "sewage-smell-outside",
          "drain-overflowing", "tree-roots-blocking-drain",
          "collapsed-drain", "standing-water-after-rain",
          "soakaway-not-working",
        ],
      },
      {
        id: "driveway",
        label: "Driveway or paving",
        problemSlugs: [
          "driveway-cracking", "block-paving-sinking",
          "weeds-growing-through-driveway", "oil-stain-on-driveway",
          "gravel-driveway-washing-away", "driveway-not-draining",
        ],
      },
      {
        id: "fencing",
        label: "Fencing or gates",
        problemSlugs: [
          "fence-panel-blown-down", "fence-post-rotting",
          "fence-leaning-over", "gate-not-closing-properly",
          "fence-damaged-by-tree",
        ],
      },
      {
        id: "security",
        label: "Security or alarms",
        problemSlugs: [
          "burglar-alarm-going-off-randomly", "cctv-not-recording",
          "alarm-keypad-not-responding", "motion-sensor-false-triggers",
          "alarm-system-beeping",
        ],
      },
      {
        id: "doors-locks",
        label: "Doors or locks",
        problemSlugs: [
          "locked-out-of-house", "key-snapped-in-lock",
          "door-lock-stiff-to-turn", "window-lock-broken",
          "front-door-not-locking", "rotten-window-frame",
        ],
      },
      {
        id: "lighting-ev",
        label: "Lighting or EV charger",
        problemSlugs: [
          "outdoor-light-not-working", "ev-charger-not-working",
          "ev-charger-tripping-electrics", "slow-ev-charging",
          "ev-charger-error-light",
        ],
      },
    ],
  },
  {
    id: "roof",
    label: "Roof",
    icon: "🏗️",
    symptoms: [
      {
        id: "tiles-damage",
        label: "Missing or damaged tiles",
        problemSlugs: [
          "roof-tiles-missing", "moss-on-roof-tiles",
          "flat-roof-blistering",
        ],
      },
      {
        id: "leak",
        label: "Leak or water damage",
        problemSlugs: [
          "leaking-roof", "flashing-coming-loose",
          "damp-patch-on-ceiling",
        ],
      },
      {
        id: "structure",
        label: "Sagging or structural",
        problemSlugs: ["sagging-roof-ridge"],
      },
      {
        id: "gutters",
        label: "Gutters or drainage",
        problemSlugs: [
          "blocked-gutter", "guttering-too-high-to-access",
          "conservatory-guttering-blocked",
        ],
      },
      {
        id: "loft",
        label: "Loft problem",
        problemSlugs: [
          "condensation-in-loft", "loft-floor-feels-bouncy",
          "cold-loft-room", "condensation-in-loft-conversion",
          "loft-room-too-hot", "loft-stairs-too-steep",
        ],
      },
      {
        id: "solar",
        label: "Solar panels",
        problemSlugs: [
          "solar-panels-not-generating", "birds-nesting-under-solar-panels",
          "solar-panel-cracked", "solar-inverter-fault",
          "solar-output-dropping", "pigeon-proofing-solar-panels",
        ],
      },
      {
        id: "chimney",
        label: "Chimney",
        problemSlugs: [
          "birds-nesting-in-chimney", "chimney-needs-repair-access",
          "smoke-coming-back-into-room",
        ],
      },
      {
        id: "pests",
        label: "Pests in loft",
        problemSlugs: ["wasp-nest-in-loft", "squirrels-in-loft"],
      },
    ],
  },
  {
    id: "garden",
    label: "Garden",
    icon: "🌳",
    symptoms: [
      {
        id: "lawn-plants",
        label: "Lawn or plants",
        problemSlugs: [
          "uneven-lawn", "lawn-turning-brown", "garden-overrun-with-moss",
          "hedge-growing-out-of-control", "weeds-taking-over-garden",
          "overgrown-garden",
        ],
      },
      {
        id: "drainage-flooding",
        label: "Drainage or flooding",
        problemSlugs: [
          "waterlogged-garden", "garden-drainage-problems",
          "standing-water-after-rain",
        ],
      },
      {
        id: "paving-patio",
        label: "Paving or patio",
        problemSlugs: [
          "patio-sinking", "patio-slabs-sinking", "paving-cracking",
          "weeds-between-paving", "patio-drainage-issues",
          "patio-slabs-stained",
        ],
      },
      {
        id: "fencing",
        label: "Fencing or gates",
        problemSlugs: [
          "fence-panel-blown-down", "fence-post-rotting",
          "fence-leaning-over", "gate-not-closing-properly",
          "fence-damaged-by-tree",
        ],
      },
      {
        id: "trees",
        label: "Trees",
        problemSlugs: [
          "tree-leaning-dangerously", "dead-branches-overhanging-house",
          "tree-roots-damaging-foundations", "tree-blocking-light",
          "tree-growing-into-power-lines", "storm-damage-to-tree",
          "trees-blocking-light",
        ],
      },
      {
        id: "pests",
        label: "Pests",
        problemSlugs: ["rats-in-garden", "japanese-knotweed-near-property"],
      },
      {
        id: "waste",
        label: "Waste or clearance",
        problemSlugs: [
          "garden-full-of-rubble", "building-waste-disposal",
        ],
      },
    ],
  },
  {
    id: "garage",
    label: "Garage",
    icon: "🚗",
    symptoms: [
      {
        id: "door",
        label: "Garage door",
        problemSlugs: [
          "garage-door-not-opening", "garage-door-off-track",
          "garage-door-opener-not-working", "garage-door-spring-broken",
          "garage-door-grinding-noise",
        ],
      },
      {
        id: "damp",
        label: "Damp or moisture",
        problemSlugs: [
          "damp-penetrating-through-wall", "rising-damp-ground-floor",
          "musty-smell-in-house",
        ],
      },
      {
        id: "clearance",
        label: "Clearance",
        problemSlugs: ["garage-full-of-junk"],
      },
      {
        id: "electrical",
        label: "Electrical",
        problemSlugs: [
          "plug-socket-not-working", "power-outage-in-one-room",
          "tripping-circuit-breaker",
        ],
      },
    ],
  },
  {
    id: "whole-house",
    label: "Whole House",
    icon: "🏡",
    symptoms: [
      {
        id: "boiler-heating",
        label: "Boiler or central heating",
        problemSlugs: [
          "boiler-making-banging-noise", "pilot-light-keeps-going-out",
          "boiler-pressure-keeps-dropping", "boiler-not-firing-up",
          "no-heating-or-hot-water", "central-heating-slow-to-warm-up",
          "thermostat-not-working", "radiator-leaking-from-valve",
          "heating-on-but-no-hot-water",
        ],
      },
      {
        id: "gas-safety",
        label: "Gas safety",
        problemSlugs: [
          "gas-smell-in-house", "yellow-flame-on-gas-hob",
          "carbon-monoxide-alarm-going-off",
        ],
      },
      {
        id: "heat-pump",
        label: "Heat pump",
        problemSlugs: [
          "heat-pump-not-heating", "heat-pump-loud-noise",
          "heat-pump-ice-build-up", "heat-pump-error-codes",
          "heat-pump-not-reaching-temperature", "heat-pump-running-constantly",
        ],
      },
      {
        id: "damp-moisture",
        label: "Damp or moisture",
        problemSlugs: [
          "rising-damp-ground-floor", "damp-patch-bottom-of-wall",
          "tide-mark-on-walls", "musty-smell-in-house",
          "wet-walls-in-basement", "salt-deposits-on-walls",
          "paint-bubbling-ground-floor",
        ],
      },
      {
        id: "insulation-energy",
        label: "Insulation or energy",
        problemSlugs: [
          "house-cold-despite-heating", "draughty-rooms-upstairs",
          "high-energy-bills", "cold-walls-to-touch",
          "ice-on-inside-of-windows", "pipes-freezing-in-winter",
        ],
      },
      {
        id: "structural",
        label: "Structural concern",
        problemSlugs: [
          "subsidence-signs", "foundation-cracks", "sagging-floor",
        ],
      },
      {
        id: "pests",
        label: "Pests",
        problemSlugs: [
          "mice-in-the-house", "bed-bugs", "moth-infestation",
          "wasp-nest-in-loft",
        ],
      },
      {
        id: "conservatory",
        label: "Conservatory",
        problemSlugs: [
          "conservatory-roof-leaking", "conservatory-too-hot",
          "conservatory-too-cold", "conservatory-door-not-closing",
          "condensation-in-conservatory",
        ],
      },
      {
        id: "survey",
        label: "Survey findings",
        problemSlugs: [
          "cracks-found-during-survey", "subsidence-in-survey",
          "damp-found-in-survey", "japanese-knotweed-near-property",
          "roof-problems-in-survey", "asbestos-found-in-survey",
        ],
      },
      {
        id: "moving",
        label: "Moving or clearance",
        problemSlugs: [
          "house-clearance-needed", "moving-day-damage-to-furniture",
          "items-too-large-for-doorway", "kitchen-refit-waste",
        ],
      },
    ],
  },
];

const tradeCostCache = new Map<string, { low: number; high: number }>();

function getTradeCostRange(tradeSlug: string): { low: number; high: number } | null {
  if (tradeCostCache.has(tradeSlug)) return tradeCostCache.get(tradeSlug)!;
  const trade = PRICING_DATA.find((t) => t.tradeSlug === tradeSlug);
  if (!trade || trade.jobTypes.length === 0) return null;
  const low = Math.round(
    trade.jobTypes.reduce((s, j) => s + j.labourLow + j.materialsLow, 0) / trade.jobTypes.length
  );
  const high = Math.round(
    trade.jobTypes.reduce((s, j) => s + j.labourHigh + j.materialsHigh, 0) / trade.jobTypes.length
  );
  const result = { low, high };
  tradeCostCache.set(tradeSlug, result);
  return result;
}

export function getMatchingProblems(
  symptomSlugs: string[],
  severity: "just-noticed" | "getting-worse" | "urgent"
): DiagnoseResult[] {
  const slugSet = new Set(symptomSlugs);
  const problems = PROBLEMS_DATA.filter((p) => slugSet.has(p.slug));

  const severityOrder: Record<string, number> = {
    urgent: 3,
    fix_soon: 2,
    cosmetic: 1,
  };

  const severityBoost: Record<string, Record<string, number>> = {
    "just-noticed": { cosmetic: 3, fix_soon: 2, urgent: 1 },
    "getting-worse": { fix_soon: 3, urgent: 2, cosmetic: 1 },
    urgent: { urgent: 3, fix_soon: 2, cosmetic: 1 },
  };

  const boostMap = severityBoost[severity];

  const scored = problems.map((p) => ({
    problem: p,
    score: (boostMap[p.urgency_level] ?? 0) + severityOrder[p.urgency_level],
  }));

  scored.sort((a, b) => b.score - a.score);

  const tradeName = (slug: string) => {
    const t = PRICING_DATA.find((tp) => tp.tradeSlug === slug);
    return t?.tradeName ?? slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return scored.slice(0, 3).map((s) => {
    const cost = getTradeCostRange(s.problem.recommended_trade_category);
    return {
      problem: s.problem,
      costLow: cost?.low ?? null,
      costHigh: cost?.high ?? null,
      tradeName: tradeName(s.problem.recommended_trade_category),
    };
  });
}
